import { NotificationsGroup, Notification, Modal, Paragraph } from '@vwfs-bronson/bronson-react';
import { loginRedirectPagePath } from '../navigation/paths';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/rootReducer';
import { useSelector, useDispatch } from 'react-redux';
import { timeOutNotificationStateActions } from './TimeOutNotificationSlice';
import { useLocation } from 'react-router-dom';
import { idleTimer } from 'utils/time';
import { useOidcAuthentication } from '@cp-shared-19/frontend-ui';

export const TimeOutNotification: React.FC = () => {
    const { t } = useTranslation('time-out-notification');
    const dispatch = useDispatch();
    const timeOutNotificationState = useSelector((state: RootState) => state.timeOutNotification);
    const [timeLeft, setTimeLeft] = useState<number>(() => idleTimer.getRemainingSeconds());
    const { isAuthenticated, logout } = useOidcAuthentication();
    const [isNotificationVisible, setIsNotificationVisible] = useState<boolean>(true);
    const [isModalShown, setIsModalShown] = useState<boolean>(false);

    const location = useLocation();

    const handleLogout = () => {
        return logout({ post_logout_redirect_uri: window.location.origin + loginRedirectPagePath() });
    };

    const closeNotification = () => {
        setIsNotificationVisible(false);
    };

    useEffect(() => {
        const searchValues = parseSearch(location.search) as Record<string, string>;
        if (searchValues?.sesion_expired === 'true') {
            dispatch(timeOutNotificationStateActions.setSessionExpired(true));
        }

        if (!isAuthenticated) {
            return;
        }
        if (isAuthenticated) {
            dispatch(timeOutNotificationStateActions.setSessionExpired(false));
            const timer = setInterval(() => {
                const secondsLeft = idleTimer.getRemainingSeconds();
                if (secondsLeft <= 0) {
                    handleLogout();
                }
                setTimeLeft(idleTimer.getRemainingSeconds());
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [isAuthenticated]);

    const displayNotification = () => {
        const minutes = Math.floor(timeLeft / 60);
        const seconds = timeLeft % 60;
        const formattedSeconds = String(seconds).padStart(2, '0');
        const timerFormatted = `${minutes}:${formattedSeconds}`;
        if (!isNotificationVisible) {
            return null;
        }
        if (timeOutNotificationState.isSessionExpired) {
            return null;
        } else if (isAuthenticated && minutes < 5 && minutes > 0) {
            return (
                <NotificationsGroup>
                    <Notification
                        title={`${t('title')} ${minutes}:${formattedSeconds}`}
                        status={minutes < 3 ? 'warning' : null}
                        onCloseClick={() => {
                            closeNotification();
                        }}
                        visible={!!isNotificationVisible}
                    ></Notification>
                </NotificationsGroup>
            );
        } else if (isAuthenticated && !minutes) {
            if (!isModalShown) {
                setIsModalShown(true);
                idleTimer.pause();
            }
            return (
                <Modal
                    shown={true}
                    title={t('modal.title')}
                    buttonConfirmText={t('modal.button')}
                    buttonCancelText={t('modal.button-logout')}
                    hideCloseButton
                    onConfirm={() => {
                        idleTimer.resume();
                        idleTimer.resetTimeout();
                        setIsModalShown(false);
                        setTimeLeft(5 * 60);
                    }}
                    onCancel={handleLogout}
                    onClose={handleLogout}
                >
                    <Paragraph>{t('modal.message')}</Paragraph>
                    <Paragraph>{t('modal.info', { timer: timerFormatted })}</Paragraph>
                </Modal>
            );
        } else {
            return <></>;
        }
    };

    return displayNotification();
};

/**
 * Parses a URI search query and returns an object.
 * @param search - The search query to parse.
 * @returns the query as a plain JS object.
 */
export function parseSearch(search: string): Record<string, string> | null {
    if (!search) {
        return null;
    }
    const searchParameters = new URLSearchParams(search);
    const result: Record<string, string> = {};

    searchParameters.forEach((value: string, key: string) => {
        result[key] = value;
    });

    return result;
}
